import { APIDataDepartmentUserThrough } from '@evertel/types'

// circular dep, rather define it the same way here
// import { DepartmentAccess } from '@evertel/departments-access'

export type DepartmentAccess = APIDataDepartmentUserThrough


//==============================================
// RETURNS A NEGATIVE ID FOR ANY LOCAL ELEMENT
//==============================================
let lastId = -1
export function localId() {
    let id = -Date.now()
    while (id === lastId) id = -Date.now()

    lastId = id
    return id
}

//==============================================
// DEEP COPY
//==============================================
export function deepCopy(objOrArray) {
    return JSON.parse(JSON.stringify(objOrArray))
}

//==============================================
// RETURN USER'S PRIMARY DEPARTMENT ACCESS including a GUEST ROLE
//==============================================
export function getPrimaryDepartmentsAccess(departmentsAccess: DepartmentAccess[]): DepartmentAccess {
    departmentsAccess = departmentsAccess || []
    const firstPrimary = departmentsAccess.find(d => d.isPrimary && d.isVerified)
    const firstNonGuestVerified = departmentsAccess.find(d => d.isVerified && getRole(d.roles) !== 'guest')
    const firstVerified = departmentsAccess.find(d => d.isVerified)

    return firstPrimary || firstNonGuestVerified || firstVerified || (departmentsAccess.length && departmentsAccess[0]) || null
}

//==============================================
// RETURN USER'S DEPARTMENT ACCESS OBJ FOR THE
// DEPT ID PASSED IN
//==============================================
export function getSpecificDepartmentsAccess (departmentsAccess, specificDepartmentId) {
    if (!departmentsAccess || !departmentsAccess.length || !specificDepartmentId) return null
    return departmentsAccess.find(d => d.departmentId === parseInt(specificDepartmentId, 10))
}

//==============================================
// RETURNS TRUE ROLE
//==============================================
export function getRole(rolesArray?: Array<string>): 'executive' | 'management' | 'employee' | 'guest' {
    if (!rolesArray || !rolesArray.length) return 'guest'

    // works because checks are in order of priority
    if (rolesArray.includes('executive')) return 'executive'
    if (rolesArray.includes('management')) return 'management'
    if (rolesArray.includes('employee')) return 'employee'
    if (rolesArray.includes('guest')) return 'guest'
    return 'guest'
}

//==============================================
// PARTITION DEPARTMENTS ACCESS FOR PROFILE DISPLAY
//==============================================
export interface PartitionedDepartments {
    primary: DepartmentAccess | null
    selected: DepartmentAccess | null
    nonGuest: DepartmentAccess[]
    guest: DepartmentAccess[]
}

/**
 * Partitions a user's departmentsAccess into groups for profile display.
 * - primary: the user's primary department (via getPrimaryDepartmentsAccess)
 * - selected: the currently selected department (null if same as primary)
 * - nonGuest: remaining non-guest departments (excluding primary & selected)
 * - guest: remaining guest departments (excluding primary & selected)
 */
export function partitionDepartmentsForProfile(
    departmentsAccess: DepartmentAccess[],
    selectedDepartmentId?: number
): PartitionedDepartments {
    if (!departmentsAccess?.length) {
        return { primary: null, selected: null, nonGuest: [], guest: [] }
    }

    const primary = getPrimaryDepartmentsAccess(departmentsAccess)

    const selectedMatch = selectedDepartmentId
        ? departmentsAccess.find(da => da.departmentId === selectedDepartmentId)
        : null
    // Only surface selected if it's different from primary
    const selected = (selectedMatch && selectedMatch.departmentId !== primary?.departmentId)
        ? selectedMatch
        : null

    const prominentIds = new Set<number>(
        [primary?.departmentId, selected?.departmentId].filter(Boolean)
    )

    const nonGuest: DepartmentAccess[] = []
    const guest: DepartmentAccess[] = []

    for (const da of departmentsAccess) {
        if (prominentIds.has(da.departmentId)) continue
        if (getRole(da.roles) === 'guest') {
            guest.push(da)
        } else {
            nonGuest.push(da)
        }
    }

    return { primary, selected, nonGuest, guest }
}

//==============================================
// RETURNS DEPARTMENT SHORT NAME
//==============================================
export function getAbbreviatedName(name: string | undefined) {
    if (!name) return ''

    const nameWithoutParenthesis = name.split('(')[0].trim()
    const words = nameWithoutParenthesis.split(' ').filter(Boolean)

    if (words.length === 1) {
        const word = words[0]
        return word.length <= 7 ? word : word.slice(0, 3) + '.'
    }

    return words
        .map(word => {
            const isAllUpper = word === word.toUpperCase()
            const isAllLower = word === word.toLowerCase()

            if (isAllUpper) return word                              // "ABC" → "ABC"
            if (isAllLower) return word[0].toUpperCase()            // "special" → "S"
            // mixed case — extract uppercase letters only
            return word.split('').filter(c => c === c.toUpperCase() && c.match(/[A-Z]/)).join('')
        })
        .join('')
}
